import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import { readAccessDeviceRegistry, type AccessDeviceRegistryItem } from './access-device-registry';
import type { DevicePurpose } from './device-purpose';
import {
  parseGrant,
  parseIssue,
  type StayEntitlementGrant,
  type StayEntitlementIssue,
} from './entitlements';

/** Each registry row contains its complete membership, including connections outside this property. */
export async function readCurrentAccessDevice(
  client: ServiceClient,
  deviceId: string,
  signal?: AbortSignal,
): Promise<AccessDeviceRegistryItem> {
  if (!isUuid(deviceId)) throw invalid();
  let cursor: string | undefined;
  const seen = new Set<string>();
  do {
    const page = await readAccessDeviceRegistry(client, { limit: 50, cursor }, signal);
    const device = page.items.find((item) => item.core_device_id === deviceId);
    if (device) return device;
    if (!page.next_cursor) break;
    if (seen.has(page.next_cursor)) throw invalid();
    seen.add(page.next_cursor);
    cursor = page.next_cursor;
  } while (!signal?.aborted);
  throw new ApiError({
    kind: 'http',
    service: 'core',
    status: 404,
    message: '현재 장비를 찾을 수 없습니다.',
  });
}

/** 저장된 물리 장비↔Unit 연결 한 건. 논리 권한 그룹이 아니며 실제 장비 적용은 항상 UNKNOWN이다. */
export interface AccessRelation {
  relation_id: string;
  device_id: string;
  unit_id: string;
  room_id: string;
  device_revision: number;
  configured_active: boolean | null;
  /** Stored purpose, absent on older servers. */
  purpose?: DevicePurpose;
  provider_application: 'UNKNOWN';
}
export interface AccessRelationPage {
  property_id: string;
  observed_at: string;
  items: AccessRelation[];
  next_cursor: string | null;
}
export interface AccessTargetStay {
  stay_id: string;
  unit_id: string;
  period_issues: { issue: StayEntitlementIssue; grants: StayEntitlementGrant[] }[];
}
export interface AccessTargetPage {
  property_id: string;
  device_id: string;
  observed_at: string;
  scope: 'CURRENT_UNIT_STORED_ISSUES';
  access_decision: 'UNKNOWN';
  provider_application: 'UNKNOWN';
  items: AccessTargetStay[];
  next_cursor: string | null;
}

type Dict = Record<string, unknown>;
const record = (v: unknown): v is Dict => !!v && typeof v === 'object' && !Array.isArray(v);
const sorted = (...names: string[]) => names.sort().join(',');
const keys = (v: Dict) => Object.keys(v).sort().join(',');
const nullableUuid = (v: unknown): v is string | null => v === null || isUuid(v);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '출입 연결 정보를 확인하지 못했습니다. 관리자에게 문의해 주세요.',
  });

function cursorOrThrow(cursor: string | null | undefined): string | undefined {
  if (cursor === null || cursor === undefined) return undefined;
  if (!isUuid(cursor)) throw invalid();
  return cursor;
}

function parseRelation(v: unknown): AccessRelation {
  if (
    !record(v) ||
    keys(v) !==
      sorted(
        'relation_id',
        'device_id',
        'unit_id',
        'room_id',
        'device_revision',
        'configured_active',
        'provider_application',
        ...(Object.hasOwn(v, 'purpose') ? ['purpose'] : []),
      ) ||
    (Object.hasOwn(v, 'purpose') &&
      v.purpose !== 'UNKNOWN' &&
      v.purpose !== 'COMMON_ENTRANCE' &&
      v.purpose !== 'ROOM_LOCK') ||
    !isUuid(v.relation_id) ||
    !isUuid(v.device_id) ||
    !isUuid(v.unit_id) ||
    !isUuid(v.room_id) ||
    !Number.isSafeInteger(v.device_revision) ||
    !(v.configured_active === null || typeof v.configured_active === 'boolean') ||
    v.provider_application !== 'UNKNOWN'
  )
    throw invalid();
  return v as unknown as AccessRelation;
}

export async function readAccessRelations(
  client: ServiceClient,
  propertyId: string,
  filter: { deviceId?: string; unitId?: string; cursor?: string | null; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AccessRelationPage> {
  const limit = filter.limit ?? 50;
  if (
    !isUuid(propertyId) ||
    (filter.deviceId !== undefined && !isUuid(filter.deviceId)) ||
    (filter.unitId !== undefined && !isUuid(filter.unitId)) ||
    !Number.isSafeInteger(limit) ||
    limit < 1 ||
    limit > 50
  )
    throw invalid();
  const v = await client.get<unknown>(`admin/properties/${propertyId}/access-relations`, {
    params: {
      limit,
      device_id: filter.deviceId,
      unit_id: filter.unitId,
      cursor: cursorOrThrow(filter.cursor),
    },
    signal,
  });
  if (
    !record(v) ||
    keys(v) !== sorted('property_id', 'observed_at', 'items', 'next_cursor') ||
    v.property_id !== propertyId ||
    !isOffsetDateTime(v.observed_at) ||
    !Array.isArray(v.items) ||
    v.items.length > limit ||
    !nullableUuid(v.next_cursor)
  )
    throw invalid();
  const items = v.items.map(parseRelation);
  if (
    items.some(
      (item) =>
        (filter.deviceId !== undefined && item.device_id !== filter.deviceId) ||
        (filter.unitId !== undefined && item.unit_id !== filter.unitId),
    ) ||
    (v.next_cursor !== null && v.next_cursor !== items.at(-1)?.relation_id)
  )
    throw invalid();
  return {
    property_id: v.property_id,
    observed_at: v.observed_at,
    items,
    next_cursor: v.next_cursor,
  };
}

function parseTargetStay(v: unknown): AccessTargetStay {
  if (
    !record(v) ||
    keys(v) !== sorted('stay_id', 'unit_id', 'period_issues') ||
    !isUuid(v.stay_id) ||
    !isUuid(v.unit_id) ||
    !Array.isArray(v.period_issues)
  )
    throw invalid();
  return {
    stay_id: v.stay_id,
    unit_id: v.unit_id,
    period_issues: v.period_issues.map((entry: unknown) => {
      if (
        !record(entry) ||
        keys(entry) !== sorted('issue', 'grants') ||
        !Array.isArray(entry.grants)
      )
        throw invalid();
      return { issue: parseIssue(entry.issue), grants: entry.grants.map(parseGrant) };
    }),
  };
}

/**
 * 장비 하나가 연결된 Unit의 투숙에 대해 저장된 발급 근거만 읽는다.
 * 지금 출입 가능 여부나 실제 장비 적용은 이 응답에 없다(access_decision·provider_application = UNKNOWN).
 */
export async function readAccessTargets(
  client: ServiceClient,
  propertyId: string,
  deviceId: string,
  cursor?: string | null,
  signal?: AbortSignal,
): Promise<AccessTargetPage> {
  if (!isUuid(propertyId) || !isUuid(deviceId)) throw invalid();
  const v = await client.get<unknown>(
    `admin/properties/${propertyId}/access-devices/${deviceId}/access-targets`,
    { params: { limit: 20, cursor: cursorOrThrow(cursor) }, signal },
  );
  if (
    !record(v) ||
    keys(v) !==
      sorted(
        'property_id',
        'device_id',
        'observed_at',
        'scope',
        'access_decision',
        'provider_application',
        'items',
        'next_cursor',
      ) ||
    v.property_id !== propertyId ||
    v.device_id !== deviceId ||
    !isOffsetDateTime(v.observed_at) ||
    v.scope !== 'CURRENT_UNIT_STORED_ISSUES' ||
    v.access_decision !== 'UNKNOWN' ||
    v.provider_application !== 'UNKNOWN' ||
    !Array.isArray(v.items) ||
    v.items.length > 20 ||
    !nullableUuid(v.next_cursor)
  )
    throw invalid();
  const items = v.items.map(parseTargetStay);
  if (v.next_cursor !== null && v.next_cursor !== items.at(-1)?.stay_id) throw invalid();
  return {
    property_id: v.property_id,
    device_id: v.device_id,
    observed_at: v.observed_at,
    scope: 'CURRENT_UNIT_STORED_ISSUES',
    access_decision: 'UNKNOWN',
    provider_application: 'UNKNOWN',
    items,
    next_cursor: v.next_cursor,
  };
}
